/**
 * ## Utils X
 *
 * Contains a collection of Miscellaneous utilities.
 *
 * ### Example
 *
 * #### Interface
 *     import {x} from 'utils';
 *
 * @module module:modules/utils/x
 *
 * @public
 **/
const x = {};

/**
 * Will return a function that wraps an original function, which when called makes sure yet
 * another function is invoked before it.
 *
 * @example <caption>Trigger functions in order</caption>
 *     function two() {
 *         console.log('2');
 *     }
 *
 *     function one() {
 *         console.log('1');
 *     }
 *
 *     var wrapperFunction = x.callBefore(two, one);
 *     wrapperFunction();
 *     // output:
 *     // 1
 *     // 2
 *
 * @param {Function} orig Original function
 * @param {Function} before Function to invoke before original
 *
 * @return {Function} Wrapper function that will invoke the `before` function before the `original` function
 *
 * @memberof module:modules/utils/x
 *
 * @function callBefore
 *
 * @instance
 *
 * @public
 **/
x.callBefore = function (orig, before) {
    return function (...args) {
        try {
            before && before();
        } finally {
            orig && orig.apply(undefined, args);
        }
    };
};

/**
 * Will return a function that wraps an original function, which when called makes sure yet
 * another function is invoked after it.
 *
 * @example <caption>Trigger functions in order</caption>
 *     function two() {
 *         console.log('2');
 *     }
 *
 *     function one() {
 *         console.log('1');
 *     }
 *
 *     var wrapperFunction = x.callAfter(two, one);
 *     wrapperFunction();
 *     // output:
 *     // 2
 *     // 1
 *
 * @param {Function} orig Original function
 * @param {Function} after Function to invoke before original
 *
 * @return {Function} Wrapper function that will invoke the `after` function after the `original` function
 *
 * @memberof module:modules/utils/x
 *
 * @function callAfter
 *
 * @instance
 *
 * @public
 **/
x.callAfter = function (orig, after) {
    return function (...args) {
        try {
            orig && orig.apply(undefined, args);
        } finally {
            after && after();
        }
    };
};

/**
 * Will return a wrapper function that will only invoke the target function once.
 * Each additional call will make sure to not invoke the target function.
 *
 * @example <caption>Only run target function once</caption>
 * function target() {
 *     console.log('invoked');
 * }
 * var onceWrapper = x.once(target);
 * onceWrapper();
 * // 'invoked' will be outputted
 * onceWrapper();
 * // nothing will be outputted
 *
 * @param {Function} fn Function to be run once
 *
 * @returns {Function} A wrapper function that only allows the original to be invoked once
 *
 * @memberof module:modules/utils/x
 *
 * @function once
 *
 * @instance
 *
 * @public
 **/
x.once = function (fn) {
    let done = false;
    return function onced(...args) {
        return done ? undefined : ((done = true), fn.apply(this, args));
    };
};

/**
 * Will return a wrapper function that will only invoke the target function after a number of invocations.
 * Each call earlier to this point will make sure to not invoke the target function.
 *
 * @example <caption>Run target function from third invocation</caption>
 * function target() {
 *     console.log('invoked');
 * }
 * var timesWrapper = x.after(3, target);
 * timesWrapper();
 * // nothing will be outputted
 * timesWrapper();
 * // nothing will be outputted
 * timesWrapper();
 * // 'invoked' will be outputted
 * timesWrapper();
 * // 'invoked' will be outputted
 *
 * @param {Number} times Number of invocations to ignore
 * @param {Function} func Function to be run after the amount of times specified above
 *
 * @returns {Function} A wrapper function that only allows the original to be invoked after a number of invocations
 *
 * @memberof module:modules/utils/x
 *
 * @function after
 *
 * @instance
 *
 * @public
 **/
x.after = function (times, func) {
    return function (...args) {
        times -= 1;
        if (times <= 0) {
            return func.apply(this, args);
        }
    };
};

/**
 * Will return a wrapper function that will invoke the target function asynchronously.
 *
 * @example <caption>Run target function asynchronously</caption>
 * function target() {
 *     console.log('invoked');
 * }
 * var asyncWrapper = x.async(target);
 * asyncWrapper();
 * // asyncWrapper will not block the main thread, but run asynchronously.
 *
 * @param {Function} fn Function to run asynchronously.
 *
 * @param {Number} [delay] Amount of milliseconds to delay the execution of fn. If no value is specified, zero milliseconds delay is used.
 *
 * @returns {Function} A wrapper function that, once invoked, will trigger the target function to run asynchronously.
 *
 * @memberof module:modules/utils/x
 *
 * @function async
 *
 * @instance
 *
 * @public
 **/
x.async = (fn, delay) => {
    return function (...args) {
        setTimeout(function () {
            fn.apply(null, args);
        }, delay || 0);
    };
};

/**
 * Will return a wrapper function that, as long as it continues to be invoked too often, will not be triggered.
 *
 * @example <caption>Function not stressed, running as soon as possible</caption>
 * console.time('debounce');
 * var functionToDebounce = function(){
 *     console.timeEnd('debounce');
 * };
 *
 * var debouncedFunction = x.debounce(functionToDebounce);
 * debouncedFunction();
 *
 * // Console should log:
 * // debounced: 100ms
 *
 * @example <caption>Resizing of a window, triggering once resize not in progress for one second</caption>
 * var logResize = function(){
 *     console.log('resized');
 * };
 *
 * var debouncedResize = x.debounce(logResize);
 * window.addEventListener('resize', debouncedResize);
 *
 * // If continuing resizing the window, no debug logging will be printed.
 * // Once resizing is idle for one second, the logging will be outputted.
 *
 *
 * @param {Function} fn Function to debounce
 * @param {Number} threshold Number of milliseconds to not invoke function in order for it to run. Optional, defaults to 100
 * @param {Function} scope Function to debounce. Optional, defaults to current context (whatever this is at time of invokation)
 *
 * @returns {Function} A wrapper function that, once invoked will ensure the target function is debounced
 *
 * @memberof module:modules/utils/x
 *
 * @function debounce
 *
 * @instance
 *
 * @public
 **/
x.debounce = function (fn, threshold, scope) {
    let timeout;
    threshold = threshold || 100;
    return function debounced(...args) {
        const context = scope || this;

        function delayed() {
            fn.apply(context, args);
            timeout = null;
        }

        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(delayed, threshold);
    };
};

/**
 * Will return a clone of an object. Is agnostic to if it is primitives, Arrays or Objects.
 *
 * @example <caption>Clone primitives</caption>
 * var originalNumber = 123;
 * var originalString = '123';
 * var originalBoolean = true;
 * var originalNull = null;
 *
 * var cloneNumber = x.clone(originalNumber);
 * var cloneString = x.clone(originalString);
 * var cloneBoolean = x.clone(originalBoolean);
 * var cloneNull = x.clone(originalNull);
 *
 * // All "clones" are actually the exact same instance, such that eg. cloneNumber === originalNumber
 *
 * @example <caption>Clone Objects</caption>
 * var originalArray = [1, 2, '3', null];
 * var originalObject = {'abc': 123};
 *
 * var cloneArray = x.clone(originalArray);
 * var cloneObject = x.clone(originalObject);
 *
 * // All clones are own instances of the originals, eg. cloneArray !== originalArray,
 * // but running eg. x.deepEqual(cloneArray, originalArray) would return true
 *
 * @param {String|Number|Boolean|Object|Array} obj Object to clone
 *
 * @returns {String|Number|Boolean|Object|Array} A clone of the sent in object
 *
 * @memberof module:modules/utils/x
 *
 * @function clone
 *
 * @instance
 *
 * @public
 **/
x.clone = function (obj) {
    if (!x.isObject(obj)) {
        return obj;
    }
    return Array.isArray(obj) ? obj.slice() : extend({}, obj);
};

/**
 * Extends an object with additional objects, returning a union of them.
 * If same key exists in many objects, the ones in the later objects will override the earlier ones.
 *
 * @example <caption>Simple extend of two objects</caption>
 * var objA = {'hello': 'world'};
 * @param {...any} args
 * var objB = {'goodbye': 'cruel world'};
 * var result = x.extend(objA, objB);
 *
 * // result = {
 * //     'hello': 'world',
 * //     'goodbye': 'cruel world'
 * //}
 *
 * @example <caption>Extend with overriding values</caption>
 *
 * var objA = {'123': 123, '456': 789};
 * var objB = {'456': 456, '789': 789};
 * var result = x.extend(objA, objB);
 *
 * // result = {
 * //     '123': 123,
 * //     '456': 456,
 * //     '789': 789
 * //}
 *
 * @param {...Object} obj Objects to extend
 *
 * @returns {Object} Extended result of all objects
 *
 * @memberof module:modules/utils/x
 *
 * @function extend
 *
 * @instance
 *
 * @public
 **/
const extend = (x.extend = function (obj, ...args) {
    args.forEach(function (source) {
        if (source) {
            // eslint-disable-next-line guard-for-in
            for (const prop in source) {
                obj[prop] = source[prop];
            }
        }
    });

    return obj;
});

/**
 * Evaluates if sent in parameter is an object, basically all non primitives.
 *
 * @example <caption>Checking parameters being objects</caption>
 * x.isObject(null);         // false
 * x.isObject(false);        // false
 * x.isObject(undefined);    // false
 * x.isObject(123);          // false
 * x.isObject('abc');        // false
 *
 * x.isObject({});           // true
 * x.isObject([]);           // true
 * x.isObject(function(){}); // true
 *
 * @param {*} obj Any type value to check for being an object
 *
 * @returns {Boolean} Boolean value telling if sent in parameter is a non primitive
 *
 * @memberof module:modules/utils/x
 *
 * @function isObject
 *
 * @instance
 *
 * @public
 **/
x.isObject = function (obj) {
    const type = typeof obj;
    return type === 'function' || (type === 'object' && !!obj);
};

/**
 * Returns a new object with only the keys provided in the parameter list.
 *
 * @example <caption>Picking a few keys to keep in complex object</caption>
 * var bigObject = {
 *     'big': 'object,
 *     'with': 'many',
 *     'keys': 'inside',
 *     'keep': 'this'
 * };
 * var pickedObject = x.pick(bigObject, objB);
 *
 * // result = {
 * //     'hello': 'world',
 * //     'goodbye': 'cruel world'
 * //}
 *
 * @param {Object} obj Source object
 * @param {...String} keysToPick keys to pick out from source object
 *
 * @returns {Object} Object with only specified keys inside
 *
 * @memberof module:modules/utils/x
 *
 * @function pick
 *
 * @instance
 *
 * @public
 **/
x.pick = function (obj, ...keysToPick) {
    if (!x.isObject(obj)) {
        return {};
    }
    const result = {};
    keysToPick.forEach(function (key) {
        if (key in obj) {
            result[key] = obj[key];
        }
    });
    return result;
};

/**
 * Returns a new object than ensures that default values are specified in the original object.
 *
 * @example <caption>Default values in empty object</caption>
 * var original = {};
 * var defaultValues = {'contrast': 0.95, 'brightness': 0.89};
 * var originalWithDefaults = x.defaults(original, defaultValues);
 *
 * // originalWithDefaults = defaultValues
 *
 * @example <caption>Only non defined default values override</caption>
 * var original = {'contrast': 0.76};
 * var defaultValues = {'contrast': 0.95, 'brightness': 0.89};
 * var originalWithDefaults = x.defaults(original, defaultValues);
 *
 * // originalWithDefaults = {'contrast': 0.76, 'brightness': 0.89}
 *
 * @example <caption>Default overrides in many levels</caption>
 * var original = {'contrast': 0.76};
 * var defaultValues1 = {'contrast': 0.95, 'brightness': 0.89};
 * var defaultValues2 = {'brightness': 0.89, 'saturation': 1.00};
 * var originalWithDefaults = x.defaults(original, defaultValues1, defaultValues2);
 *
 * // originalWithDefaults = {'contrast': 0.76, 'brightness': 0.89, 'saturation': 1.00}
 *
 * @param {Object} obj Objects to ensure default values specified
 * @param {...String} defaults Objects with default values
 *
 * @returns {Object} Object with default values ensured
 *
 * @memberof module:modules/utils/x
 *
 * @function defaults
 *
 * @instance
 *
 * @public
 **/
x.defaults = function (obj, ...defaults) {
    if (!x.isObject(obj)) {
        return obj;
    }
    defaults.forEach(function (source) {
        if (x.isObject(source)) {
            Object.keys(source).forEach(function (key) {
                if (!(key in obj)) {
                    obj[key] = source[key];
                }
            });
        }
    });
    return obj;
};

const toString = Object.prototype.toString;

/**
 * Evaluates if sent in parameter is a function.
 *
 * @example <caption>Checking parameters being function</caption>
 * x.isFunction(null);         // false
 * x.isFunction(false);        // false
 * x.isFunction(undefined);    // false
 * x.isFunction(123);          // false
 * x.isFunction('abc');        // false
 * x.isFunction({});           // false
 * x.isFunction([]);           // false
 *
 * x.isFunction(function(){}); // true
 *
 * @param {*} obj Any type value to check for being a function
 *
 * @returns {Boolean} Boolean value telling if sent in parameter is a function
 *
 * @memberof module:modules/utils/x
 *
 * @function isFunction
 *
 * @instance
 *
 * @public
 **/
x.isFunction = function (obj) {
    return !!obj && toString.call(obj) === '[object Function]';
};
function isObjectLike(value) {
    return !!value && typeof value === 'object';
}

/**
 * Evaluates if sent in parameter is a string.
 *
 * @example <caption>Checking parameters being string</caption>
 * x.isString(null);         // false
 * x.isString(false);        // false
 * x.isString(undefined);    // false
 * x.isString(123);          // false
 * x.isString({});           // false
 * x.isString([]);           // false
 * x.isString(function(){}); // false
 *
 * x.isString('abc');        // true
 *
 * @param {*} value Any type value to check for being a string
 *
 * @returns {Boolean} Boolean value telling if sent in parameter is a string
 *
 * @memberof module:modules/utils/x
 *
 * @function isString
 *
 * @instance
 *
 * @public
 **/
x.isString = function (value) {
    return (
        typeof value === 'string' ||
        (isObjectLike(value) && toString.call(value) === '[object String]')
    );
};

/**
 * Throws an error if a condition is not fulfilled.
 *
 * @example <caption>Required parameter present</caption>
 * var requiredParameter = 'settings';
 * var parameters = {'settings': 'veryImportant'};
 * x.assert(requiredParameter in parameters, 'Missing required settings parameter');
 * // Will not throw an error
 *
 * @example <caption>Required parameter missing</caption>
 * var requiredParameter = 'settings';
 * var parameters = {'optionals': 'notImportant'};
 * x.assert(requiredParameter in parameters, 'Missing required settings parameter');
 * // Will throw an error
 *
 * @param {Boolean} condition Condition that should be true
 * @param {String} message Message to throw if condition is not fulfilled
 *
 * @memberof module:modules/utils/x
 *
 * @function assert
 *
 * @instance
 *
 * @public
 **/
x.assert = function (condition, message) {
    if (!condition) {
        throw new Error(message);
    }
};

export default x;
